import { type IChartApi, type ISeriesApi, createChart, ColorType, LineSeries } from "lightweight-charts";
import { Panel } from "./panel-base.ts";
import { el } from "../utils/dom.ts";
import { apiGet } from "../services/api-client.ts";

interface SeriesResp {
  points: Array<{ day: string; avg: number; count: number }>;
  excludedDays: number;
}

// 30-day daily-average sentiment line. Baseline at 0. Includes a fixed bias note:
// Vietnamese press skews positive, so the signal is in the *trend*, not the absolute level.
export class SentimentTrendPanel extends Panel {
  private chart: IChartApi | null = null;
  private line: ISeriesApi<"Line"> | null = null;
  private readonly chartHost: HTMLElement;

  constructor() {
    super({ id: "sentiment-trend", title: "Sắc thái dư luận", subtitle: "30 ngày" });
    this.chartHost = el("div", { className: "chart-host" });
    const note = el("div", { className: "sentiment-bias-note" }, [
      "Sắc thái phản ánh giọng điệu báo chí (thường thiên tích cực) — giá trị nằm ở xu hướng thay đổi, không phải mức tuyệt đối.",
    ]);
    this.setContent(this.chartHost, note);
  }

  async load(): Promise<void> {
    try {
      const data = await apiGet<SeriesResp>("/api/v1/sentiment/series?days=30");
      if (data.points.length === 0) {
        this.setContent(
          el("div", { className: "panel-empty" }, ["Dữ liệu sắc thái đang tích lũy."]),
        );
        return;
      }
      const chart = this.ensureChart();
      this.line?.setData(
        data.points.map((p) => ({
          time: p.day as never, // 'YYYY-MM-DD' is a valid lightweight-charts business day
          value: p.avg,
        })),
      );
      chart.timeScale().fitContent();
    } catch {
      this.setError();
    }
  }

  private ensureChart(): IChartApi {
    if (this.chart) return this.chart;
    this.chart = createChart(this.chartHost, {
      height: 240,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#9aa4b2",
        attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { color: "#1e2733" } },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false },
      autoSize: true,
    });
    this.line = this.chart.addSeries(LineSeries, {
      color: "#7cc4ff",
      lineWidth: 2,
      baseLineVisible: true,
    });
    return this.chart;
  }
}
